import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import * as Fonts from "../../../../components/Fonts";
import { LoadingModal } from "../../../../components/LoadingModal";
import { SubHeader } from "../../../../components/SubHeader";
import { api } from "../../../../services/api";
import {
  BillingDetails,
  BillingDetailsValue,
  BillingErrors,
  EMPTY_BILLING,
  validateBilling,
  withSavedBilling,
} from "../Checkout/BillingDetails";
import { Actions, Container, Panel, Section } from "./styles";
import { useManagedSubscription, useSave } from "./useManagedSubscription";

/** Who's paying, as it goes on the receipts: name, CPF/CNPJ or tax ID, and address. */
export const BillingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefix = "pages.subscription.manage.billing";
  const { detail, isLoading, detailUrl, siteId } = useManagedSubscription();
  const { state, error, save } = useSave(detailUrl, navigate);

  const [value, setValue] = useState<BillingDetailsValue>(EMPTY_BILLING);
  const [errors, setErrors] = useState<BillingErrors>({});

  // This Keepo's own billing details (each PLUS Keepo is billed on its own); for the ones that don't have them
  // yet, the account's last used at checkout, or else the account's name
  useEffect(() => {
    if (isLoading) {
      return;
    }
    api
      .getUser()
      .then(user => setValue(current => withSavedBilling(current, detail?.billing || user.billing, user.name)))
      .catch(() => undefined);
    // Once, when the subscription has loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (isLoading) {
    return <LoadingModal />;
  }

  const change = (next: BillingDetailsValue) => {
    setValue(next);
    if (Object.keys(errors).length) {
      setErrors(validateBilling(next));
    }
  };

  const submit = () => {
    const nextErrors = validateBilling(value);
    setErrors(nextErrors);
    if (!Object.keys(nextErrors).length) {
      save(() => api.updateBilling(siteId, { ...value }));
    }
  };

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={[
          { label: t("pages.plusBenefits.title"), to: "/keepo/plus" },
          { label: t("pages.subscriptionDetail.title"), to: detailUrl },
        ]}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.label`)}</Fonts.Label>
        <Panel>
          <p className="intro">{t(`${prefix}.description`)}</p>
          <BillingDetails value={value} onChange={change} errors={errors} withBrazilFields />
        </Panel>
      </Section>

      <Actions>
        {error && (
          <p className="error" role="alert">
            {t(`pages.subscription.manage.errors.${error}`)}
          </p>
        )}
        {state === "done" ? (
          <p className="done">{t(`${prefix}.saved`)}</p>
        ) : (
          <button
            type="button"
            className="primary"
            disabled={state === "saving"}
            aria-busy={state === "saving"}
            onClick={submit}>
            {t(`${prefix}.save`)}
          </button>
        )}
        <button type="button" className="secondary" onClick={() => navigate(detailUrl)}>
          {t("pages.subscription.manage.cancel")}
        </button>
      </Actions>
    </Container>
  );
};
